/**
 * ChangelogButton — a small version badge in the topbar that opens a modal
 * listing what changed across releases. Data comes from src/data/changelog.ts.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_VERSION, CHANGELOG } from "../../data/changelog";

export const ChangelogButton: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const lang: "en" | "ja" = i18n.language.startsWith("ja") ? "ja" : "en";

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        className="changelog-badge"
        onClick={() => setOpen(true)}
        aria-label={t("changelog.open")}
        title={t("changelog.open")}
      >
        v{APP_VERSION}
      </button>

      {open && (
        <div
          className="changelog-modal__overlay"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="changelog-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t("changelog.title")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="changelog-modal__header">
              <h2>{t("changelog.title")}</h2>
              <button
                className="changelog-modal__close"
                onClick={() => setOpen(false)}
                aria-label={t("changelog.close")}
              >
                ✕
              </button>
            </div>

            <div className="changelog-modal__body">
              {CHANGELOG.map((entry) => (
                <section key={entry.version} className="changelog-entry">
                  <div className="changelog-entry__head">
                    <span className="changelog-entry__version">v{entry.version}</span>
                    <span className="changelog-entry__date">{entry.date}</span>
                  </div>
                  <ul className="changelog-entry__list">
                    {entry.changes.map((change, i) => (
                      <li key={i}>{change[lang]}</li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
